import { useWizardStore } from '@/store/wizardStore'
import type { AutonomyLevel } from '@/store/wizardStore'
import type { Lang } from '@/hooks/useLang'

interface Step4AIProps { lang: Lang }

const LEVELS: {
  level: AutonomyLevel
  icon: string
  fr: { title: string; desc: string; tag: string }
  en: { title: string; desc: string; tag: string }
}[] = [
  {
    level: 1,
    icon: 'keyboard',
    fr: { title: 'Téléopéré', desc: 'Chaque action requiert une commande humaine directe.', tag: 'Manuel' },
    en: { title: 'Teleoperated', desc: 'Every action requires a direct human command.', tag: 'Manual' },
  },
  {
    level: 2,
    icon: 'assistant',
    fr: { title: 'Assisté', desc: 'L\'IA propose des actions ; l\'humain valide avant exécution.', tag: 'Semi-auto' },
    en: { title: 'Assisted', desc: 'AI suggests actions; human confirms before execution.', tag: 'Semi-auto' },
  },
  {
    level: 3,
    icon: 'smart_toy',
    fr: { title: 'Supervisé', desc: 'Exécution autonome dans un périmètre défini, supervision humaine.', tag: 'Supervisé' },
    en: { title: 'Supervised', desc: 'Autonomous execution within defined perimeter, human oversight.', tag: 'Supervised' },
  },
  {
    level: 4,
    icon: 'psychology',
    fr: { title: 'Autonome', desc: 'Prise de décision indépendante avec escalade sur cas critiques.', tag: 'Haute auto.' },
    en: { title: 'Autonomous', desc: 'Independent decision-making with escalation on critical cases.', tag: 'High auto.' },
  },
  {
    level: 5,
    icon: 'auto_awesome',
    fr: { title: 'Pleinement autonome', desc: 'Opération totalement indépendante, apprentissage continu.', tag: 'Full AI' },
    en: { title: 'Fully autonomous', desc: 'Fully independent operation with continuous learning.', tag: 'Full AI' },
  },
]

export default function Step4AI({ lang }: Step4AIProps) {
  const { draft, update } = useWizardStore()
  const selected = draft.step4_ai_level

  return (
    <div className="space-y-4">
      {/* Visual scale indicator */}
      <div className="flex items-center gap-2 mb-8">
        <span className="text-xs text-on-surface-variant font-label tracking-widest uppercase">
          {lang === 'fr' ? 'Manuel' : 'Manual'}
        </span>
        <div className="flex-1 h-0.5 bg-gradient-to-r from-surface-container-highest via-primary-container/40 to-secondary rounded-full" />
        <span className="text-xs text-on-surface-variant font-label tracking-widest uppercase">
          {lang === 'fr' ? 'Plein IA' : 'Full AI'}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
        {LEVELS.map((l) => {
          const isSelected = selected === l.level
          const intensity = l.level / 5
          return (
            <div
              key={l.level}
              onClick={() => update({ step4_ai_level: l.level })}
              className={`glass-card p-6 rounded-xl border border-outline-variant/20 flex flex-col gap-3 cursor-pointer ${isSelected ? 'ring-1 ring-primary-container bg-surface-container-highest/80' : ''}`}
            >
              {/* Level number */}
              <div className="flex items-center justify-between">
                <span
                  className="font-headline text-3xl font-bold"
                  style={{ opacity: 0.3 + intensity * 0.7, color: isSelected ? '#00f0ff' : '#dae2fd' }}
                >
                  {l.level}
                </span>
                <span className={`px-2 py-0.5 rounded text-[9px] font-label tracking-widest uppercase ${isSelected ? 'bg-primary-container/20 text-primary-container' : 'bg-surface-container text-on-surface-variant'}`}>
                  {l[lang].tag}
                </span>
              </div>
              <span className="material-symbols-outlined text-2xl text-primary/60">{l.icon}</span>
              <h4 className={`font-headline text-base font-bold ${isSelected ? 'text-primary-container' : 'text-on-surface'}`}>
                {l[lang].title}
              </h4>
              <p className="text-xs text-on-surface-variant leading-relaxed">{l[lang].desc}</p>
            </div>
          )
        })}
      </div>
    </div>
  )
}
